import React, { Component, Fragment } from 'react'
import { string, array, bool, object, number, oneOfType } from 'prop-types'
import {
  FORMS, FIELDS_IN_COOKIE, TYPE_OF_REQUEST, REQUEST_SAMPLE,
  WORLD_CITY_ID, COUNTRY, LOCATION, GEO_IP, EMPTY_OBJECT, RECAPTCHA_RESPONSE, FORM_WITH_PHONE, PHONE, ACCOUNT_ID
} from '../../helpers/constants'
import { getFormHiddenFields, getFormattedCities, getBatchQuotesURL, prefixInfoRequestFields, getCurrentCountry } from '../../helpers/form-helpers'
import { sendIRTrackingEvents, scrollToElement, getDevice } from '../../helpers/util'
import { persistValueToCookies, getValuesFromCookies } from '../../helpers/cookies'
import { insertValuesInTranslation } from '../../helpers/string-helpers'
import { insightsAttrs } from '../../utils/insights'
import withUserData from '../../hoc/withUserData'
import { validateField, validateForm, flattenErrors, renderRecaptcha, executeRecaptcha } from '../../helpers/validator'
import AlertMessage, { ALERT_TYPES } from '../ui/alert-message'
import Button from '../ui/buttons/button'
import TermsAndConditions from '../ui/form-elements/terms-and-conditions'
import FormLayout from './FormLayout'
import LoadingForm from './LoadingForm'

const translations = { ...NFT.I18n.single_info_request }

const SINGLE_QUOTE_RULES = {
  name: 'required|string|min:3',
  country: 'required|string',
  location: 'required|string',
  branch: 'required|string',
  email: 'required|email',
  details: 'required|min:10',
  company_website: 'required|custom_url|min:5|max:70' /* Custom_url validation is in validator.js */
}

const INITIAL_FORM_VALUES = {
  name: '',
  email: '',
  country: {},
  location: {},
  details: '',
  request_a_sample: 0,
  branch: '',
  company_website: '',
  typeOfRequest: NFT.I18n.single_info_request.type_of_request_options.price_quote
}

const EXCLUDED_FIELDS = [TYPE_OF_REQUEST, REQUEST_SAMPLE, ...(!FORM_WITH_PHONE.includes(NFT.site) ? [PHONE] : [])]

class SingleQuoteForm extends Component {
  constructor(props) {
    super(props)
    this.device = props.device.toUpperCase()
    this.recaptcha = {}
    this.insightsValues = insightsAttrs({
      category: 'info-request',
      label: 'submit',
      value: props.isContact ? props.account_id : props.product_id,
      funnelConversion: props.isContact ? 'AC-quote' : 'quote'
    })
    this.companyName = props.company_name
    this.productName = props.product_name
    this.adMedium = props.ad_medium
    
    const { locationOptions = [], location = {}, phone = '' } = props.user
    let { name = '', email = '', country = {} } = props.user
    const cookiesValues = getValuesFromCookies() || {}
    const companyWebsite = cookiesValues?.company_website ?? ""
    this.currentCountry = getCurrentCountry(props.countryOptions) || {}

    if (Object.keys(country)?.length === 0 && Object.keys(this.currentCountry)?.length > 0) {
      country = this.currentCountry
    }

    // Set Name and Email with cookies or with myAD data
    if (name === '' || email === '') {
      if (Object.keys(cookiesValues)?.length) {
        ({ email = '', name = '' } = cookiesValues)
      } else if (Object.keys(AD?.CurrentUser)?.length) {
        email = AD.CurrentUser['email'] || ''
        name = AD.CurrentUser['full_name'] || ''
      }
    }

    const details = this.getDetailsTranslation('price_quote', { company: this.companyName, product: this.productName, userName: name })

    this.isUserInfoCollapsed = name.length && email.length && !!country.value && !!location.value
    this.state = {
      locationOptions: locationOptions,
      formValues: { ...INITIAL_FORM_VALUES, name, email, country, location, phone, details, includeCompanyWebsite: true, companyWebsite },
      formErrors: EMPTY_OBJECT,
      submitting: false,
      userName: name
    }
  }

  getDetailsTranslation = (typeOfRequest, values) => {
    const requestFrom = !!values.product ? 'type_of_request_value' : 'company_type_of_request_value'
    return insertValuesInTranslation(translations[requestFrom][typeOfRequest], { ...values })
  }

  componentDidMount() {
    const { formValues } = this.state
    const { country } = { ...formValues }
    const hasSetCurrentCountryData = !!Object.keys(this.currentCountry)?.length

    if (hasSetCurrentCountryData && country.value === this.currentCountry.value) {
      this.setStateLocationOptions(this.currentCountry.value)
    } else if (!hasSetCurrentCountryData && Object.keys(country)?.length === 0) {
      const { countryOptions } = this.props
      // @TODO Move (this) the logic of default country to HOC
      window.addEventListener('afd:initCountryCode', () => {
        const country = getCurrentCountry(countryOptions)
        this.setState({ formValues: { ...formValues, country } })
        this.setStateLocationOptions(country.value)
        this.currentCountry = country
      })
    }
  }

  setStateLocationOptions = countryCode => {
    getFormattedCities(countryCode).then(locationOptions => {
      this.setState({ locationOptions })
    })
  }

  validateForm = e => {
    e.preventDefault()
    this.setState({ submitting: true })
    const { formValues } = this.state
    const { isContact } = this.props
    const { country, location } = formValues
    const formErrors = validateForm({ ...formValues, country: country ? country.value : '', location: location ? location.value : '' },
      EXCLUDED_FIELDS, SINGLE_QUOTE_RULES)
    if (formErrors.errorCount) {
      this.setState({ submitting: false, formErrors: flattenErrors(formErrors) }, () => {
        const firstError = this.wrapper.querySelector('.has-error')
        const offset = isContact ? 0 : this.device === 'DESKTOP_SIDEBAR' ? 8 : -8
        scrollToElement(firstError, true, offset)
      })
      return
    }
    if (this.recaptcha[this.device]) executeRecaptcha(FORMS[`SINGLE_QUOTE_${this.device}`])
  }

  submitForm = async recaptchaToken => {
    const { formValues: { country, location, ...otherFields } = {} } = this.state
    const { submit_url: submitUrl, product_id: productId, account_id: accountId, isContact } = this.props
    const allFields = {
      ...otherFields,
      ...getFormHiddenFields(this.adMedium),
      [COUNTRY]: country.value,
      [LOCATION]: location.value,
      [WORLD_CITY_ID]: location.world_city_id,
      [GEO_IP]: country.value
    }

    if (isContact) allFields[ACCOUNT_ID] = accountId

    const infoRequest = { ...prefixInfoRequestFields(allFields), [RECAPTCHA_RESPONSE]: recaptchaToken, company_name: '' }
    const formData = new FormData()
    Object.entries(infoRequest).forEach(([field, value]) => formData.append(field, value))

    const response = await fetch(submitUrl, { method: 'POST', body: formData })
    const { token, ...messages } = await response.json()

    if (response.ok) {
      if (isContact) {
        this.setState({
          feedbackStatus: ALERT_TYPES.SUCCESS,
          feedbackMessage: messages.message,
          submitting: false
        })
      } else {
        sendIRTrackingEvents('sent', productId)
        window.location.replace(getBatchQuotesURL(token))
      }
      return
    }
    const { details, error } = messages
    this.setState({
      feedbackStatus: ALERT_TYPES.ERROR,
      feedbackMessage: (details && details.join('\n')) || error,
      submitting: false
    })
  }

  onFieldChangeHandler = async (fieldName, fieldValue) => {
    let updatedCities, textArea
    if (FIELDS_IN_COOKIE.includes(fieldName)) {
      persistValueToCookies(fieldName, fieldValue.value || fieldValue, 'nft_info_request', 365)
      if (fieldName === COUNTRY) {
        updatedCities = await getFormattedCities(fieldValue.value)
      }
    }

    const { userName, formErrors, locationOptions, formValues } = this.state

    if (fieldName === 'typeOfRequest') {
      const [key] = Object.entries(translations.type_of_request_options).find(([_, value]) => value === fieldValue)
      textArea = this.getDetailsTranslation(key, { company: this.companyName, product: this.productName, userName })
    }
    
    const updatedErrors = !EXCLUDED_FIELDS.includes(fieldName)
      ? { ...formErrors, [fieldName]: validateField({ [fieldName]: fieldValue }, true) }
      : formErrors
    const updatedFormValues = { ...formValues, [fieldName]: fieldValue, ...(textArea && { details: textArea }) }
    this.setState({
      locationOptions: updatedCities || locationOptions,
      formValues: updatedFormValues,
      formErrors: updatedErrors
    })
  }

  initRecaptcha = el => {
    this.recaptcha[this.device] = el
    const { recaptcha_position: position = 'bottomright' } = this.props
    renderRecaptcha(this.recaptcha[this.device], this.submitForm, FORMS[`SINGLE_QUOTE_${this.device}`], 'image', 'invisible', position)
  }

  render() {
    const { locationOptions, formValues, formErrors: errors, submitting, feedbackMessage, feedbackStatus, prefillMessage } = this.state

    const { company_name: companyName,
      request_sample: sampleAvailable,
      custom_terms_conditions: customTermsAndConditions,
      is_gallery: isGalleryForm,
      fixed_submit: fixedSubmit,
      countryOptions,
      renderOnly
    } = this.props

    const currentDevice = getDevice()

    if (renderOnly && renderOnly !== currentDevice) return null

    const fixedSubmitBtn = fixedSubmit || (isGalleryForm && !this.isUserInfoCollapsed)

    const formProps = {
      countryOptions,
      locationOptions,
      formValues,
      errors,
      companyName,
      sampleAvailable,
      isGalleryForm: isGalleryForm && !this.isUserInfoCollapsed,
      onFieldChangeHandler: this.onFieldChangeHandler,
      prefillMessage
    }

    return (
      <Fragment>
        <h2 className='afd-title-widget nft-form__title'>{NFT.I18n.single_info_request.title}</h2>
        <div className='nft-form__wrapper' ref={el => { this.wrapper = el }}>
          {!!Object.keys(this.currentCountry)?.length &&
            <AlertMessage type={'information'} message={translations.available_in_country} customSvg={'mapMarker'} highLightMessage={this.currentCountry?.label} shouldScroll={false} />}
          {feedbackMessage &&
            <AlertMessage type={feedbackStatus} message={feedbackMessage}
              title={feedbackStatus === ALERT_TYPES.SUCCESS ? NFT.I18n.sent : NFT.I18n.error} />}
          <form onSubmit={this.validateForm} data-insights-funnel-scenario='AC-IR-Form'>
            <FormLayout {...formProps} />
            <div ref={this.initRecaptcha}
              className={isGalleryForm ? 'recaptcha-container--gallery' : ''} />
            <div className={fixedSubmitBtn ? 'nft-submit--sticky' : ''}>
              <Button type='submit'
                label={submitting ? translations.submitting : translations.send_message}
                disabled={submitting}
                insightsValues={this.insightsValues} />
              <TermsAndConditions
                customTermsAndConditions={customTermsAndConditions}
                companyName={companyName} />
            </div>
          </form>
        </div>
      </Fragment>
    )
  }
}

SingleQuoteForm.propTypes = {
  product_name: string,
  company_name: string,
  ad_medium: string,
  product_id: oneOfType([string, number]),
  account_id: oneOfType([string, number]),
  countries: array,
  countryOptions: array,
  submit_url: string,
  request_sample: bool,
  device: string,
  isContact: bool,
  custom_terms_conditions: string,
  is_gallery: bool,
  fixed_submit: bool,
  recaptcha_position: string,
  user: object,
  renderOnly: string
}

export default withUserData(SingleQuoteForm, LoadingForm)
